import { ChevronDownIcon } from 'lucide-react';
import { useId, useState, type JSX } from 'react';

import type { ProposalCase } from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

/**
 * The "Coverage" block for one generated case: a plain-language statement of what the case verifies
 * plus the sources it cites (source name, an optional location within it, and an optional short
 * verbatim quote). It's its own collapsible section — collapsed by default — nested inside the case
 * row's expanded body. Returns null when the case carries neither coverage nor citations, so it's
 * safe to drop into every row.
 */
export function CaseCoverage({ c }: { c: ProposalCase }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const citations = c.citations ?? [];
  const requirementIds = c.requirementIds ?? [];
  const hasMetadata = Boolean(
    c.priority || c.type || c.area || requirementIds.length,
  );
  if (!c.coverage && citations.length === 0 && !hasMetadata) return null;
  return (
    <div className="flex flex-col items-start gap-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={open ? bodyId : undefined}
        className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-left text-xs font-medium transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronDownIcon
          className={cn('size-3 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
        Coverage
      </button>
      {open ? (
        <div id={bodyId} className="flex flex-col gap-1 pl-4">
          {c.coverage ? (
            <p className="text-muted-foreground">{c.coverage}</p>
          ) : null}
          {hasMetadata ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
              {c.priority ? (
                <>
                  <dt className="font-medium">Priority:</dt>
                  <dd className="text-muted-foreground">{c.priority}</dd>
                </>
              ) : null}
              {c.type ? (
                <>
                  <dt className="font-medium">Type:</dt>
                  <dd className="text-muted-foreground">{c.type}</dd>
                </>
              ) : null}
              {c.area ? (
                <>
                  <dt className="font-medium">Area:</dt>
                  <dd className="text-muted-foreground">{c.area}</dd>
                </>
              ) : null}
              {requirementIds.length ? (
                <>
                  <dt className="font-medium">Requirements:</dt>
                  <dd className="text-muted-foreground">
                    {requirementIds.join(', ')}
                  </dd>
                </>
              ) : null}
            </dl>
          ) : null}
          {citations.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {citations.map((citation) => (
                <li
                  key={`${citation.source}::${citation.location ?? ''}::${citation.quote ?? ''}`}
                  className="flex flex-col gap-0.5"
                >
                  <span>
                    <span className="font-medium">{citation.source}</span>
                    {citation.location ? (
                      <span className="text-muted-foreground">
                        {' '}
                        · {citation.location}
                      </span>
                    ) : null}
                  </span>
                  {citation.quote ? (
                    <blockquote className="border-l-2 border-border pl-2 italic text-muted-foreground">
                      &ldquo;{citation.quote}&rdquo;
                    </blockquote>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
